import './painting-flow-item-edit.css';

import { IonLabel, IonList, IonListHeader } from '@ionic/react';
import { Form } from 'formik';
import { isEmpty } from 'lodash';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { ActionButton } from '../../../../../../components/button/action-button';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { CloseButton } from '../../../../../../components/button/close-button';
import { IntercomHelp } from '../../../../../../components/help/intercom-help';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { IntercomArticle } from '../../../../../../config/intercom';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useIsMounted } from '../../../../../../util/hooks';
import { logger } from '../../../../../../util/logger';
import { FlowItemMultiEditProps } from '../../../../shared/component/flow-item/edit/flow-item-edit.types';
import { FlowItemFormComponent } from '../../../../shared/component/flow-item/edit/flow-item-form';
import {
  WidgetToggler,
  WidgetTogglerProps,
} from '../../../../shared/component/widget/widget-toggler';
import {
  WidgetExtensionProps,
  WidgetExtensions,
} from '../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItem,
  FlowItemParameter,
} from '../../../../shared/model/flow.types';
import { BeaconPaintingData } from '../../model/beacon-painting-data';
import { PaintProcessSettingsForm } from './paint-process-settings-form';
import { PaintingFlowItemEditActionBar } from './painting-flow-item-edit-action-bar';

const PaintingFlowItemMultiEdit = (
  props: FlowItemMultiEditProps
): ReactElement => {
  const [saving, setSaving] = useState<boolean>(false);
  const { onUpdateItem, system, items, onClosed, onDeleteItems, readOnly } =
    props;
  const { flowAnalyticService, flowItemService, flow } = useFlowServices();

  const [modifiedRefs, setModifiedRefs] = useState<string[]>([]);
  const updateFlowConfigOnSave = useRef<boolean>(false);

  const hasFieldsSelected = modifiedRefs.length > 0;

  const hasPaintsSelected = useMemo(() => {
    return items.findIndex(
      (i) => i.ref === BeaconPaintingData.FlowItemRef.PAINT
    ) > -1
      ? true
      : false;
  }, [items]);

  const defaultPaintItem = useMemo((): FlowItem => {
    const parameters =
      flowItemService.getApplicationDefaultParameterSet()?.data.parameters ||
      [];
    const item = { parameters, ref: BeaconPaintingData.FlowItemRef.PAINT };

    return item;
  }, [flowItemService]);

  const isMounted = useIsMounted();

  const onSubmit = async (item: FlowItem) => {
    setSaving(true);

    const params = modifiedRefs.reduce((prev, ref) => {
      const path = ref.split('.');
      const params = item.parameters.find((params) => {
        return params.ref === path[1];
      });

      if (params) {
        prev.push(params);
      }
      return prev;
    }, [] as FlowItemParameter[]);
    const itemToSave = { ...item, parameters: params };

    logger.info('saving multi-edit changes');
    await onUpdateItem(itemToSave, updateFlowConfigOnSave.current);
    flowAnalyticService.trackMultiEdit(updateFlowConfigOnSave.current);

    if (isMounted()) {
      setSaving(false);
    }
  };

  const widgetExtensions = useMemo((): WidgetExtensions => {
    const onFieldToggle = (enabled: boolean, fieldname: string) => {
      if (isMounted()) {
        if (enabled) {
          if (!modifiedRefs.includes(fieldname)) {
            setModifiedRefs([...modifiedRefs, fieldname]);
          }
        } else {
          const remaining = modifiedRefs.filter((r) => r !== fieldname);
          setModifiedRefs(remaining);
        }
      }
    };

    const props = {
      onToggle: onFieldToggle,
    } as WidgetTogglerProps & WidgetExtensionProps;

    return {
      rightElement: <WidgetToggler {...props} />,
    };
  }, [isMounted, modifiedRefs]);

  const itemHeaderTitle = getLocalizedText('welding:multi_edit_items_header', {
    numOfItems: items.length,
  });

  return (
    <FlowItemFormComponent
      onSubmit={onSubmit}
      item={defaultPaintItem}
      hasFlowChanges={hasFieldsSelected && items.length > 0}
    >
      {({ errors, setValidationSchema, submitForm }) => {
        const isReadyToSubmit =
          hasFieldsSelected && items.length > 0 && isEmpty(errors);

        return (
          <div className={'flow-panel-container'}>
            <IonListHeader className={'panel-header'}>
              <IonLabel>{itemHeaderTitle}</IonLabel>
              <IntercomHelp
                articleId={IntercomArticle.MULTI_EDIT_CUT_SETTINGS}
              />
            </IonListHeader>
            <div className="header-divider" />
            <Form>
              <PaintingFlowItemEditActionBar
                items={items}
                readOnly={readOnly}
                onDelete={async (itemsForDelete) => {
                  const indices: number[] = itemsForDelete.map(
                    (itemForDelete) => {
                      return flow.items.findIndex(
                        (i) => i.id === itemForDelete.id
                      );
                    }
                  );
                  onDeleteItems(indices);
                }}
              />
              {hasPaintsSelected && (
                <PaintProcessSettingsForm
                  system={system}
                  extensions={readOnly ? undefined : widgetExtensions}
                  parameterOptions={{
                    ...BeaconPaintingData.DefaultPaintParameterMetadataFromFormOptions,
                    forceIncludeTags: [
                      ...(BeaconPaintingData
                        .DefaultPaintParameterMetadataFromFormOptions
                        .forceIncludeTags ?? []),
                    ],
                    forceExcludeRefs: [
                      BeaconPaintingData.FlowItemParameterRef.QNEAR,
                      BeaconPaintingData.FlowItemParameterRef.WAYPOINT,
                    ],
                  }}
                  setValidationSchema={setValidationSchema}
                  // always default readOnly to true since multi-edit leverages the widget toggler
                  readOnly={true}
                />
              )}
              <RightAlignedButtons>
                <CloseButton
                  onClose={() => {
                    if (typeof onClosed === 'function') {
                      onClosed();
                    }
                  }}
                />

                <ActionButton
                  label={getLocalizedText('common:save_options')}
                  buttonProps={{
                    color: isReadyToSubmit ? 'primary' : 'medium',
                    disabled: !isReadyToSubmit || saving,
                    fill: isReadyToSubmit ? 'solid' : 'outline',
                  }}
                >
                  {({ close }) => {
                    return (
                      <IonList lines="full">
                        <IonItem
                          onClick={(e) => {
                            e.preventDefault();
                            updateFlowConfigOnSave.current = false;
                            submitForm();
                            close();
                          }}
                          href="#"
                          detail={false}
                        >
                          {getLocalizedText('common:update_these_only')}
                        </IonItem>
                        <IonItem
                          lines="none"
                          onClick={(e) => {
                            e.preventDefault();
                            updateFlowConfigOnSave.current = true;
                            submitForm();
                            close();
                          }}
                          href="#"
                          detail={false}
                        >
                          {getLocalizedText(
                            'common:update_these_and_part_defaults'
                          )}
                        </IonItem>
                      </IonList>
                    );
                  }}
                </ActionButton>
              </RightAlignedButtons>
            </Form>
          </div>
        );
      }}
    </FlowItemFormComponent>
  );
};

export { PaintingFlowItemMultiEdit };
